import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from "@angular/core";
import * as Validation from "../../../../shared/constants/validation.constants";
import {FormControl, FormGroup} from "@angular/forms";
import {CommonService} from "../../../../shared/services/common.service";
import {Subject, takeUntil} from "rxjs";
import {Store} from "@ngrx/store";
import * as fromRoot from "../../../../../state/app.state";
import {Invite} from "../../services/invite";
import {roles} from "../../../../shared/constants/common.constants";
import {
  autogeneratedUsername,
  verifyUsername
} from "../../../../shared/core/shared.selectors";
import {
  ClearAutoGeneratedUsername
} from "src/app/modules/shared/core/shared.actions";

@Component({
  selector: 'app-new-user',
  templateUrl: './new-user.component.html',
  styleUrls: ['./new-user.component.css']
})
export class NewUserComponent implements OnInit, OnDestroy {
  @Input() index: number;
  @Input() uuid: string;
  @Input() selectedRole: number;
  @Input() form: FormGroup = new FormGroup<any>({});
  @Input() errorMessageMap: any = {};
  @Input() errorMessages: any = {};
  @Input() isParticipant = false;
  @Output() larChange = new EventEmitter<number>();
  verifyStatusIcon = null;
  isVerifyDisabled = false;
  generatedUsername: any;
  @Input() maxDobDate: any;
  @Input() minDobDate: any;
  displayErrorField: boolean = true;
  validation = Validation;
  isUsernameAvailable: boolean = false;
  usernameStatusLoading: boolean = false;
  displayMessage: boolean = false;
  protected readonly roles = roles;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private commonService: CommonService,
    private store: Store<fromRoot.State>,
    private inviteService: Invite
  ) {
  }

  ngOnInit() {
    this.store.dispatch(ClearAutoGeneratedUsername());
    this.inviteService.hasAssociatedLar.pipe(takeUntil(this.onDestroy))
      .subscribe((hasAssociatedLar) => {
        if (this.selectedRole === roles['adult']) {
          if (!hasAssociatedLar) {
            this.form.addControl(
              'isLar',
              new FormControl({value: false, disabled: false})
            );
          } else {
            this.form.removeControl('isLar');
          }
        }
      });

    this.form.controls['isLar']?.valueChanges.pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.larChange.emit(this.index);
      });

    this.form.controls['firstName']?.valueChanges.pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data && this.form.controls['lastName'].value) {
          this.form.controls['username'].enable();
        } else {
          this.form.controls['username'].disable();
          this.verifyStatusIcon = null;
        }
      });

    this.form.controls['lastName']?.valueChanges.pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data && this.form.controls['firstName'].value) {
          this.form.controls['username'].enable();
        } else {
          this.form.controls['username'].disable();
          this.verifyStatusIcon = null;
        }
      });

    this.form.controls['username']?.valueChanges.pipe(takeUntil(this.onDestroy))
      .subscribe(username => {
        this.usernameStatusLoading = false;
        this.verifyStatusIcon = null;
        this.isVerifyDisabled = false;
        this.displayMessage = false;
        this.inviteService.isVerifyDisabled.next(this.isVerifyDisabled);
        if (this.generatedUsername && this.generatedUsername !== username) {
          this.inviteService.markUsernameAsUnverified.next({uuid: this.uuid});
        }

        if (this.generatedUsername && this.generatedUsername === username) {
          this.inviteService.markUsernameAsVerified.next({uuid: this.uuid});
        }
      });

    this.store.select(autogeneratedUsername).pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data.uuid == this.uuid) {
          this.usernameStatusLoading = data?.loading;
          this.generatedUsername = data?.username;

          this.form.patchValue({
            username: data?.username
          }, {emitEvent: false});
          this.isVerifyDisabled = true;
          this.inviteService.isVerifyDisabled.next(this.isVerifyDisabled);
        }
      });

    this.store.select(verifyUsername).pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data.uuid == this.uuid) {
          if (data.loading) {
            this.verifyStatusIcon = null;
          } else {
            if (data.isAvailable) {
              this.verifyStatusIcon = 'check-circle';
            } else {
              this.verifyStatusIcon = 'exclamation-circle';
            }

            this.isVerifyDisabled = true;
            this.inviteService.isVerifyDisabled.next(this.isVerifyDisabled);
          }
          this.usernameStatusLoading = data?.loading;
          this.isUsernameAvailable = data?.isAvailable;
        }
      });

    this.inviteService.resetExistingUserForm
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          this.form.reset({}, {emitEvent: false});
        }
      });

    this.inviteService.disableForm
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          this.form.disable();
        }
      });
  }


  autoGenerateUsername() {
    this.commonService.generateUsername({
      firstName: this.form.controls['firstName'].getRawValue().trim(),
      lastName: this.form.controls['lastName'].getRawValue().trim(),
      uuid: this.uuid
    });
  }

  verifyUsername() {
    if (this.form?.controls?.['username']?.errors?.['spaceCheckFailed']) {
      this.form.controls['username'].markAsTouched();
      this.errorMessages['username'] = this.errorMessageMap['username'].spaceCheckFailed;
      this.displayErrorField = true;
      return;
    }

    if (this.form?.controls?.['username']?.errors?.['specialCharCheckFailed']) {
      this.form.controls['username'].markAsTouched();
      this.errorMessages['username'] = this.errorMessageMap['username'].specialCharCheckFailed;
      this.displayErrorField = true;
      return;
    }

    if (this.form?.controls?.['username']?.errors?.['minlength']) {
      this.form.controls['username'].markAsTouched();
      this.errorMessages['username'] = this.errorMessageMap['username'].minlength;
      this.displayErrorField = true;
      return;
    }

    if (this.form?.controls?.['username']?.errors?.['digitsAtStartCheckFailed']) {
      this.form.controls['username'].markAsTouched();
      this.errorMessages['username'] = this.errorMessageMap['username'].digitsAtStartCheckFailed;
      this.displayErrorField = true;
      return;
    }
    this.displayErrorField = false;

    const name = this.form.controls['username'].getRawValue();
    this.displayMessage = !!name.length;
    this.commonService.verifyUsername({
      firstName: this.form.controls['firstName'].getRawValue(),
      lastName: this.form.controls['lastName'].getRawValue(),
      username: this.form.controls['username'].getRawValue(),
      uuid: this.uuid
    });
  }

  checkForErrors(currentField?) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField))
    };
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
